import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../mock';
import { Mail, Lock, Building, User, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Register = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { login } = useAuth();
  const t = translations[lang]?.auth || translations.en.auth;
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company_name: '',
    full_name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accountType, setAccountType] = useState('individual');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (accountType === 'company') {
        const res = await axios.post(`${API}/compass/org/signup`, formData);
        login(res.data.access_token, res.data.user);
        navigate('/portal');
      } else {
        await axios.post(`${API}/auth/register`, formData);
        navigate('/verify-2fa', { state: { email: formData.email } });
      }
    } catch (err) {
      setError(err.response?.data?.detail || t.registerFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1 className="auth-logo">KRYPTON9</h1>
            <h2 className="heading-2">{t.createTitle}</h2>
            <p className="body-medium text-secondary">{t.createSubtitle}</p>
          </div>

          {error && <div className="auth-error">{error}</div>}

          <div className="form-group" style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setAccountType('individual')} className={accountType === 'individual' ? 'btn-primary' : 'btn-secondary'} style={{ flex: 1 }}>{lang === 'nl' ? 'Individueel' : 'Individual'}</button>
            <button type="button" onClick={() => setAccountType('company')} className={accountType === 'company' ? 'btn-primary' : 'btn-secondary'} style={{ flex: 1 }}>{lang === 'nl' ? 'Bedrijf' : 'Company'}</button>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label className="body-small">{t.fullNameLabel}</label>
              <div className="input-with-icon">
                <User size={18} />
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder={t.fullNamePlaceholder}
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="body-small">{t.companyLabel}</label>
              <div className="input-with-icon">
                <Building size={18} />
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder={t.companyPlaceholder}
                  data-testid="register-company-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="body-small">{t.emailLabel}</label>
              <div className="input-with-icon">
                <Mail size={18} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder={t.emailPlaceholder}
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="body-small">{t.passwordLabel}</label>
              <div className="input-with-icon">
                <Lock size={18} />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="••••••••"
                  minLength="6"
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary auth-btn" data-testid="register-submit-btn">
              {loading ? t.creatingAccount : t.createAccount}
              <ArrowRight size={16} />
            </button>
          </form>

          <div className="auth-footer">
            <p className="body-small">
              {t.alreadyAccount}{' '}
              <Link to="/login" className="auth-link">{t.signIn}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
